import * as helios from "@hyperionbt/helios";
import cx from "clsx";
import {
  GalleryArtwork,
  SecondaryOfferEntry,
} from "kreate-common/modules/business-types";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { safeParseKreateUtxoOffer } from "kreate-common/modules/utxo-parser/by-type/offer";
import React from "react";

import { getDisplayNameForGalleryArtwork } from "../../../../../../../PageGalleryCollector/utils";
import { runFlow$AcceptOffer } from "../../../../generators/runFlow$AcceptOffer";
import { runFlow$CancelOffer } from "../../../../generators/runFlow$CancelOffer";
import Definition from "../../../../modals/ModalListAsset/components/Definition";

import { OffererNameViewer } from "./components/OffererNameViewer";
import styles from "./index.module.scss";

import { useCardanoWalletApi } from "@/modules/cardano-wallet";
import { useWithErrorHandler } from "@/modules/common-hooks/useWithErrorHandler";
import {
  formatLovelaceAmount$Compact,
  formatLovelaceAmount$Standard,
} from "@/modules/common-utils/format";
import { useModalTrigger$ModalAsyncGenerator } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  offer: SecondaryOfferEntry;
  galleryArtwork: GalleryArtwork;
};

export default function SecondaryOfferEntryViewer({
  className,
  style,
  offer,
  galleryArtwork,
}: Props) {
  const wallet = useCardanoWalletApi();

  const modalTriggers = {
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
    ModalFailure: useModalTrigger$ModalFailure(),
  };

  const { busy, withErrorHandler } = useWithErrorHandler((error) =>
    modalTriggers.ModalFailure.openModalWithError(error)
  );

  const offererStakeAddress =
    offer.utxo.datum.type === "offer"
      ? helios.StakeAddress.fromAddress(
          helios.Address.fromBech32(offer.utxo.datum.value.offererAddress)
        )
      : undefined;

  const actorStakeAddress =
    wallet.type === "ready"
      ? helios.StakeAddress.fromAddress(wallet.baseAddress)
      : undefined;

  const isActorOfferer =
    offererStakeAddress &&
    actorStakeAddress &&
    offererStakeAddress.toBech32() === actorStakeAddress.toBech32();

  const parsedUtxo = safeParseKreateUtxoOffer(offer.utxo).result;

  const handleAccept = withErrorHandler(async () => {
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$AcceptOffer({
        cip30Wallet,
        offer,
        txMessage: `Offer for『${getDisplayNameForGalleryArtwork(
          galleryArtwork
        )}』was accepted!`,
      })
    );
  });

  const handleCancel = withErrorHandler(async () => {
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();
    await modalTriggers.ModalAsyncGenerator.openModal(() =>
      runFlow$CancelOffer({
        cip30Wallet,
        offer,
        txMessage: `Offer for『${getDisplayNameForGalleryArtwork(
          galleryArtwork
        )}』was just cancelled!`,
      })
    );
  });

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col gap="20px">
        <Flex.Row gap="32px">
          <Definition
            label="Price"
            content={formatLovelaceAmount$Compact(
              parsedUtxo?.datumOffer.editionPrice
            )}
            tooltip={formatLovelaceAmount$Standard(
              parsedUtxo?.datumOffer.editionPrice
            )}
          />
          <Definition
            label="Offerer"
            content={
              <OffererNameViewer
                className={styles.link}
                entry={offer}
                unstyled
              />
            }
          />
        </Flex.Row>
        <Flex.Row gap="8px">
          {isActorOfferer ? (
            <ButtonSecondary
              content="Cancel Offer"
              onClick={handleCancel}
              disabled={busy}
            />
          ) : (
            <ButtonSecondary
              content="View Offer"
              onClick={handleAccept}
              disabled={busy}
            />
          )}
        </Flex.Row>
      </Flex.Col>
    </div>
  );
}
